/**
 * Zod input schemas: outreach. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
// ── Outreach (Sep 2026) ────────────────────────────────────────────────────
export const OutreachTemplateSaveSchema = z.object({
    id: z.string().trim().min(1).max(64).nullish(),
    name: z.string().trim().min(1).max(120),
    subject: z.string().trim().min(1).max(300),
    body: z.string().min(1).max(20000),
    bodyHtml: z.string().max(200000).nullish(),
    shared: z.boolean().nullish(),
});
export const OutreachTemplateDeleteSchema = z.object({ id: z.string().trim().min(1).max(64) });
export const OutreachSfdcRefSchema = z.object({
    contactId: z.string().max(18).nullish(), leadId: z.string().max(18).nullish(),
    accountId: z.string().max(18).nullish(), opportunityId: z.string().max(18).nullish(),
    contactName: z.string().max(200).nullish(), accountName: z.string().max(200).nullish(), opportunityName: z.string().max(200).nullish(),
});
export const OutreachSendSchema = z.object({
    to: z.array(z.string().trim().min(3).max(320)).min(1).max(5),
    cc: z.array(z.string().trim().email()).max(5).nullish(),
    subject: z.string().trim().min(1).max(300),
    /** Plain text (blank-line paragraphs), already merged client-side for preview; the server re-renders. */
    body: z.string().min(1).max(20000),
    /** Rich HTML from the editor (sanitised server-side); wins over `body` when present. */
    bodyHtml: z.string().max(200000).nullish(),
    templateId: z.string().max(64).nullish(),
    sfdc: OutreachSfdcRefSchema.nullish(),
    /** Recipient merge context (first/last/company) chosen in the composer. */
    recipient: z.object({ firstName: z.string().max(80).nullish(), lastName: z.string().max(80).nullish(), company: z.string().max(200).nullish() }).nullish(),
    track: z.boolean().nullish(),
});
export const OutreachSfdcSearchSchema = z.object({
    query: z.string().trim().min(2).max(120),
    /** 'email' = exact-address lookup in email fields (auto-attach as you type);
     *  'opportunities' = open + recent opportunities of the account in `query` (an account id). */
    mode: z.enum(['name', 'email', 'opportunities']).nullish(),
});
export const OutreachPrefsSetSchema = z.object({ autoLog: z.boolean().nullish(), signature: z.string().max(4000).nullish() });
/** Do-not-track list (recipient opted out): add / remove one address. */
export const OutreachDoNotTrackSetSchema = z.object({ email: z.string().trim().toLowerCase().email(), on: z.boolean() });
/** Campaigns = sequences (Sep 2026). Save the definition (stages first, content
 *  per stage), enrol recipients later — from an SFDC tabular report (any report
 *  with an Email column) or a CSV/XLSX file (dormant behind OUTREACH_FILE_IMPORT_ENABLED). */
export const OutreachStageSchema = z.object({
    id: z.string().trim().min(1).max(32),
    type: z.enum(['email', 'task']),
    delayDays: z.number().int().min(0).max(365),
    subject: z.string().max(300).nullish(),
    body: z.string().max(50000).nullish(),
    bodyHtml: z.string().max(200000).nullish(),
    taskSubject: z.string().max(200).nullish(),
    taskNote: z.string().max(2000).nullish(),
});
export const OutreachCampaignSaveSchema = z.object({
    id: z.string().trim().min(1).max(64).nullish(),
    name: z.string().trim().min(1).max(120),
    description: z.string().max(2000).nullish(),
    stages: z.array(OutreachStageSchema).max(12),
    track: z.boolean().nullish(),
    businessHoursOnly: z.boolean().nullish(),
});
export const OutreachCampaignSourceSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('sfdcReport'), reportId: z.string().trim().regex(/^00O[A-Za-z0-9]{12}([A-Za-z0-9]{3})?$/, 'Not a Salesforce report id (starts with 00O).') }),
    z.object({ type: z.literal('file'), fileName: z.string().trim().min(1).max(200), base64: z.string().min(1).max(4_000_000) }),
]);
export const OutreachCampaignEnrolSchema = z.object({ id: z.string().trim().min(1).max(64), source: OutreachCampaignSourceSchema });
export const OutreachCampaignActionSchema = z.object({
    id: z.string().trim().min(1).max(64),
    action: z.enum(['activate', 'pause', 'archive', 'delete']),
});
/** Per-recipient control: stop (no more steps), resume, success (stop — goal reached), skipStage (jump past the current step). */
export const OutreachRecipientActionSchema = z.object({
    id: z.string().trim().min(1).max(64), recipientId: z.string().trim().min(1).max(64),
    action: z.enum(['stop', 'resume', 'success', 'skipStage']),
});
//# sourceMappingURL=outreach.js.map